/**
 * Actual COGS — ingredient cost of what was REALLY sold, not what a recipe
 * theoretically costs.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ CONTRACT: this module MUST mirror the Postgres inventory-deduction logic.
 *
 * Source of truth (latest definitions):
 *   - `_deduct_recipe`, `_line_cogs_amd` → supabase/migrations/0076_cogs_wac_snapshots.sql
 *   - `deduct_inventory_on_close`       → supabase/migrations/0076_cogs_wac_snapshots.sql
 *
 * If you change how inventory is deducted (recipe items, sub-recipes,
 * prep yields, swaps, choice groups) in a migration, update
 * `lineActualCogs()` below the same way AND `_line_cogs_amd()` in SQL,
 * or Business Health COGS will drift from reality. See .cursor/rules/recipe-cogs-sync.mdc.
 *
 * Costing model (migration 0076+):
 *   - Receipts blend purchase price into ingredients.cost_price (weighted average).
 *   - On order close the trigger snapshots WAC onto each inventory_transactions.unit_cost
 *     and the line total onto order_items.cogs_amd — frozen forever.
 *   - Business Health prefers those snapshots; `lineActualCogs()` is the fallback
 *     for legacy rows where cogs_amd / unit_cost is null.
 *
 * P&L food cost uses Σ order-line COGS (aligned with revenue). The inventory
 * ledger is shown as a reconciliation check only.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { fetchPaged } from '../fetchPaged.js';

interface RecipeItemRow {
  id: string;
  recipe_id: string;
  ingredient_id: string | null;
  sub_recipe_id: string | null;
  quantity: number;
}

interface ModifierEntry {
  groupId?: string;
  optionId?: string;
}

export interface CogsContext {
  /** product_id → recipe_id */
  recipeByProduct: Map<string, string>;
  /** recipe_id → its fixed BOM lines */
  itemsByRecipe: Map<string, RecipeItemRow[]>;
  /** recipe_id → { is_prep, prep_yield } */
  recipeMeta: Map<string, { is_prep: boolean; prep_yield: number | null }>;
  /** `${recipe_item_id}:${substitute_ingredient_id}` → substitute quantity */
  substituteQty: Map<string, number>;
  /** `${choice_group_id}:${ingredient_id}` → option deduction quantity */
  choiceOptionQty: Map<string, number>;
  /** ingredient_id → current cost_price (AMD per stock unit) */
  ingredientCost: Map<string, number>;
}

/** Load everything needed to cost order lines. One round of small queries. */
export async function loadCogsContext(db: SupabaseClient, clientId: string): Promise<CogsContext> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyDb = db as any;
  const [prodsRes, recipesRes, itemsRes, subsRes, optsRes, ingsRes] = await Promise.all([
    db.from('products').select('id, recipe_id').eq('client_id', clientId),
    db.from('recipes').select('id, is_prep, prep_yield').eq('client_id', clientId),
    db
      .from('recipe_items')
      .select('id, recipe_id, ingredient_id, sub_recipe_id, quantity')
      .eq('client_id', clientId),
    anyDb
      .from('recipe_item_substitutes')
      .select('recipe_item_id, ingredient_id, quantity')
      .eq('client_id', clientId),
    anyDb
      .from('recipe_choice_options')
      .select('group_id, ingredient_id, quantity')
      .eq('client_id', clientId),
    db.from('ingredients').select('id, cost_price').eq('client_id', clientId),
  ]);

  const recipeByProduct = new Map<string, string>();
  for (const p of prodsRes.data ?? []) {
    if (p.recipe_id) recipeByProduct.set(p.id, p.recipe_id);
  }

  const itemsByRecipe = new Map<string, RecipeItemRow[]>();
  for (const it of (itemsRes.data ?? []) as RecipeItemRow[]) {
    if (!itemsByRecipe.has(it.recipe_id)) itemsByRecipe.set(it.recipe_id, []);
    itemsByRecipe.get(it.recipe_id)!.push(it);
  }

  const recipeMeta = new Map<string, { is_prep: boolean; prep_yield: number | null }>();
  for (const r of recipesRes.data ?? []) {
    recipeMeta.set(r.id, {
      is_prep: Boolean((r as { is_prep?: boolean }).is_prep),
      prep_yield: (r as { prep_yield?: number | null }).prep_yield ?? null,
    });
  }

  const substituteQty = new Map<string, number>();
  type SubRow = { recipe_item_id: string; ingredient_id: string; quantity: number | null };
  for (const s of (subsRes.data ?? []) as SubRow[]) {
    if (s.quantity != null) substituteQty.set(`${s.recipe_item_id}:${s.ingredient_id}`, Number(s.quantity));
  }

  const choiceOptionQty = new Map<string, number>();
  type OptRow = { group_id: string; ingredient_id: string; quantity: number };
  for (const o of (optsRes.data ?? []) as OptRow[]) {
    choiceOptionQty.set(`${o.group_id}:${o.ingredient_id}`, Number(o.quantity ?? 1));
  }

  const ingredientCost = new Map<string, number>();
  for (const i of ingsRes.data ?? []) {
    ingredientCost.set(i.id, Number(i.cost_price ?? 0));
  }

  return { recipeByProduct, itemsByRecipe, recipeMeta, substituteQty, choiceOptionQty, ingredientCost };
}

const ING_SWAP_PREFIX = 'ing_swap:';
const CHOICE_PREFIX = 'choice_group:';

/**
 * Cost of one sold order line, mirroring `deduct_inventory_on_close`.
 * `modifiers` is the raw jsonb array stored on `order_items.modifiers`.
 */
export function lineActualCogs(
  ctx: CogsContext,
  productId: string,
  lineQuantity: number,
  modifiers: unknown,
): number {
  const mods: ModifierEntry[] = Array.isArray(modifiers) ? (modifiers as ModifierEntry[]) : [];

  // Swap map: original_ingredient_id → substitute_ingredient_id
  const swaps = new Map<string, string>();
  for (const m of mods) {
    if (m.groupId?.startsWith(ING_SWAP_PREFIX) && m.optionId) {
      swaps.set(m.groupId.slice(ING_SWAP_PREFIX.length), m.optionId);
    }
  }

  let cost = 0;

  // 1. Fixed BOM (mirrors `_deduct_recipe`)
  const walk = (recipeId: string, multiplier: number, depth: number) => {
    if (depth > 10) return;
    for (const it of ctx.itemsByRecipe.get(recipeId) ?? []) {
      if (it.sub_recipe_id) {
        const meta = ctx.recipeMeta.get(it.sub_recipe_id);
        const yieldDiv = meta?.is_prep && meta.prep_yield ? meta.prep_yield : 1;
        walk(it.sub_recipe_id, (multiplier * Number(it.quantity)) / yieldDiv, depth + 1);
      } else if (it.ingredient_id) {
        const subId = swaps.get(it.ingredient_id);
        if (subId) {
          const qty = ctx.substituteQty.get(`${it.id}:${subId}`) ?? Number(it.quantity);
          cost += (ctx.ingredientCost.get(subId) ?? 0) * qty * multiplier;
        } else {
          cost += (ctx.ingredientCost.get(it.ingredient_id) ?? 0) * Number(it.quantity) * multiplier;
        }
      }
    }
  };
  const recipeId = ctx.recipeByProduct.get(productId);
  if (recipeId) walk(recipeId, lineQuantity, 0);

  // 2. Chosen options (wine pours etc.)
  for (const m of mods) {
    if (!m.groupId?.startsWith(CHOICE_PREFIX) || !m.optionId) continue;
    const groupId = m.groupId.slice(CHOICE_PREFIX.length);
    const optQty = ctx.choiceOptionQty.get(`${groupId}:${m.optionId}`);
    if (optQty === undefined) continue; // option deleted — trigger skips too
    cost += (ctx.ingredientCost.get(m.optionId) ?? 0) * optQty * lineQuantity;
  }

  return cost;
}

/**
 * Prefer the sale-time snapshot on the order line; fall back to recomputing
 * from current WAC for legacy orders closed before migration 0076.
 */
export function orderLineCogs(
  ctx: CogsContext,
  productId: string,
  lineQuantity: number,
  modifiers: unknown,
  cogsAmd: number | null | undefined,
): number {
  if (cogsAmd != null && !Number.isNaN(Number(cogsAmd))) return Number(cogsAmd);
  return lineActualCogs(ctx, productId, lineQuantity, modifiers);
}

export interface LedgerCogs {
  /** AMD value of ingredients consumed by sales / force-closes in the window. */
  consumed: number;
  /** AMD value returned to stock by refunds in the window. */
  refunded: number;
  /** consumed − refunded — the P&L COGS figure. */
  net: number;
}

/**
 * COGS from the `inventory_transactions` ledger — what actually left the shelf.
 * Uses snapshotted unit_cost when present; falls back to current WAC for legacy rows.
 */
export async function loadLedgerCogs(
  db: SupabaseClient,
  clientId: string,
  fromIso: string,
  toIso: string,
  ingredientCost: Map<string, number>,
): Promise<LedgerCogs> {
  type TxRow = {
    ingredient_id: string;
    delta: number;
    type: string;
    note: string | null;
    unit_cost: number | null;
  };
  const rows = await fetchPaged<TxRow>((from, to) =>
    db
      .from('inventory_transactions')
      .select('ingredient_id, delta, type, note, unit_cost')
      .eq('client_id', clientId)
      .in('type', ['sale', 'force_close', 'restock'])
      .gte('created_at', fromIso)
      .lte('created_at', toIso)
      .order('created_at', { ascending: true })
      .range(from, to),
  );

  let consumed = 0;
  let refunded = 0;
  for (const tx of rows) {
    const unitCost =
      tx.unit_cost != null ? Number(tx.unit_cost) : (ingredientCost.get(tx.ingredient_id) ?? 0);
    const value = Math.abs(Number(tx.delta)) * unitCost;
    if (tx.type === 'sale' || tx.type === 'force_close') consumed += value;
    else if (tx.type === 'restock' && tx.note === 'refund') refunded += value;
  }
  return { consumed, refunded, net: consumed - refunded };
}
